import React, { useContext, useMemo, useState } from 'react'
import { localStorage } from 'react-storage'
import { makeStyles } from 'tss-react/mui'
import { productDetailRoute, useRouter } from '@src/routes'
import CircleLoader from '@src/components/CircleLoader'
import {
  ButtonBase,
  Link as MUILink,
  ListItem,
  Typography,
} from '@mui/material'
import {
  SubstanceProductFieldsFragment,
  SubstanceFieldsFragment,
} from '@src/queries/ProductSearchQuery.generated'
import HandleMarkup from '@src/components/HandleMarkup'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from '@src/components/Link'
import { determineCatalogType } from '@utils/searchUtils'
import ResponsiveProductPAModal from '@src/components/ResponsiveProductPAModal'
import ReplacementProductsCarousel from '@src/components/ReplacementProductsCarousel'
import { CarouselProductTypes } from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'
import useResponsiveSizes from '@src/utils/useResponsiveSizes'
import DiscontinuedProductAlert from '@src/components/DiscontinuedProductAlert/DiscontinuedProductAlert'
import { usePricingAndAvailabilityLazyQuery } from '@src/queries/PricingAndAvailabilityQuery.generated'
import { CatalogType } from '@src/types/graphql-types'
import { SitePreference, useCurrentUser } from '@src/utils/useCurrentUser'
import ProductPriceAvailabilityError from '@src/components/ProductPriceAvailability/ProductPriceAvailabilityError'
import {
  TrackedAvailabilityMessageContext,
  TrackedAvailabilityMessageProvider,
} from '@src/components/TrackedAvailabilityMessage/TrackedAvailabilityMessageContext'
import { sendProductClickEvent } from '@src/utils/analytics'
import { sendPricingAvailabilityInteractionEvent } from '@src/utils/analytics/pricingAndAvailability'
import { conformReplacementProductToCard } from '@src/components/ReplacementProductsCarousel/replacementProductToProductCard'
import TxnBadge from '@src/components/TxnBadge/'
import getConfig from 'next/config'

const {
  publicRuntimeConfig: { featureFlags },
} = getConfig()

export const useStyles = makeStyles()((theme) => {
  return {
    root: {
      display: 'block',
      padding: theme.spacing(4),
    },
    name: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
    },
    description: {
      fontSize: theme.typography.pxToRem(14),
      marginBottom: theme.spacing(4),
    },
    viewPricingWrapper: {
      textAlign: 'right',
    },
    viewPricingWrapperWithBadge: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    viewPricing: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.primary.main,
    },
    loaderContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 300,
      width: '100%',
    },
  }
})

// FIND-1123 requirements
const TXN_BADGE_TYPE = 'MAX_DISCOUNT'
const TXN_BADGE_ORGID = '7950'

interface ProductProps {
  product: SubstanceProductFieldsFragment
  substanceName?: SubstanceFieldsFragment['name']
  className?: string
  substanceId?: string
  substancePosition?: number
}

const ResponsiveSubstanceProduct: React.FC<ProductProps> = ({
  product,
  substanceName,
  className,
  substanceId,
  substancePosition,
}) => {
  const { classes, cx } = useStyles()
  const router = useRouter()
  const getSize = useResponsiveSizes()
  const { formatMessage } = useIntl()
  const { focus = '', catalogType = '', orgId = '' } = router.query || {}
  const [isModalOpen, setIsModalOpen] = useState(false)
  const marketplace = product?.isMarketplace ? CatalogType.Marketplace : null
  const {
    currentUser,
    getSitePreference,
    isB2BUser,
    isDTAQZuCustomer,
    isBlueErpIntegrationEnabled,
  } = useCurrentUser()
  const preferredDealerId = isDTAQZuCustomer
    ? getSitePreference(SitePreference.PreferredDealerId)
    : null
  const passERPType =
    isBlueErpIntegrationEnabled &&
    product?.erp_type &&
    product?.erp_type?.length > 0

  const querySearchTerm: string = localStorage?.getItem('searchedfor') ?? ''

  const [fetchPricing, { data, loading, error }] =
    usePricingAndAvailabilityLazyQuery({
      variables: {
        ...(passERPType && { erp_type: product?.erp_type }),
        brand: product?.brand?.key,
        catalogType: determineCatalogType(marketplace || catalogType || focus),
        checkBuyNow: true,
        checkForPb: true,
        dealerId: preferredDealerId,
        displaySDS: false,
        materialIds: product?.materialIds,
        orgId,
        productKey: product?.productKey,
        productNumber: product?.productNumber,
        quantity: 1,
      },
    })
  const { discontinuedPricingInfo } = data?.getPricingForProduct ?? {}
  const showModal = !loading && isModalOpen && !discontinuedPricingInfo

  // Conforms replacement products to the shape of a carousel product if they are present.
  const replacementProducts = conformReplacementProductToCard(
    discontinuedPricingInfo?.replacementProducts
  )

  const onToggleModal = (): void => {
    setIsModalOpen(!isModalOpen)
  }

  const handleClick = (): void => {
    !product.paMessage && fetchPricing()
    setIsModalOpen(true)
    sendPricingAvailabilityInteractionEvent({
      action: 'view p&a',
      section: substancePosition ? undefined : 'products',
      component: 'list',
      elementType: 'link',
      elementText: 'view',
      material: {
        materialDescription: product.name,
        brand: product.brand?.key,
        product: product.productNumber,
      },
    })
  }

  const productNumberLabel = product.productNumber.startsWith('CB_')
    ? formatMessage({ id: 'DETAILS', defaultMessage: 'Details' })
    : product.productNumber

  const ctx = useContext(TrackedAvailabilityMessageContext)

  const handleProductClick = (): void =>
    sendProductClickEvent({
      product,
      searchTerm: querySearchTerm,
      substanceId,
      substancePosition,
    })

  const salesOrg: string = useMemo(() => {
    return (
      currentUser?.soldToPartners?.[0]?.salesOrg ||
      currentUser?.metadata.salesOrg ||
      ''
    )
  }, [currentUser])
  const country: string = useMemo(() => {
    return currentUser?.metadata?.country || ''
  }, [currentUser])

  const showTxnBadge =
    featureFlags.srpTxnBadges &&
    country === 'US' &&
    !isB2BUser &&
    product.txnBadge &&
    product.txnBadge?.type === TXN_BADGE_TYPE &&
    product.txnBadge?.salesOrgIds?.includes(TXN_BADGE_ORGID) &&
    salesOrg === TXN_BADGE_ORGID

  if (error) {
    return (
      <TrackedAvailabilityMessageProvider
        {...ctx}
        brand={product.brand.key}
        item={product.name}
      >
        <ProductPriceAvailabilityError
          productNumber={product?.productNumber}
          error={error}
        />
      </TrackedAvailabilityMessageProvider>
    )
  }

  return (
    <ListItem component="li" className={cx(className, classes.root)}>
      <Typography component="h3" className={classes.name}>
        {product.brand.key && product.productNumber ? (
          <Link
            {...productDetailRoute.index(
              product.brand.key,
              product.productKey,
              { catalog: determineCatalogType(catalogType || focus) }
            )}
            passHref
          >
            <MUILink onClick={handleProductClick}>{productNumberLabel}</MUILink>
          </Link>
        ) : (
          <span>{product.productNumber}</span>
        )}
      </Typography>
      <Typography component="p" className={classes.description}>
        <HandleMarkup value={product.legalNameSuffix || product.description} />
      </Typography>
      {!!discontinuedPricingInfo ? (
        <>
          <DiscontinuedProductAlert
            discontinuedPricingInfo={discontinuedPricingInfo}
          />
          {replacementProducts && (
            <ReplacementProductsCarousel
              type={CarouselProductTypes.RecommendedProducts}
              preloadCardImages
              products={replacementProducts}
              slidesToShow={getSize({ xs: 1, sm: 2, lg: 3 })}
              withoutControls={replacementProducts.length <= 4}
            />
          )}
        </>
      ) : (
        <div
          className={
            showTxnBadge
              ? classes.viewPricingWrapperWithBadge
              : classes.viewPricingWrapper
          }
        >
          {showTxnBadge && <TxnBadge showTooltip toolTipPlacement="right" />}

          <ButtonBase
            className={classes.viewPricing}
            onClick={handleClick}
            aria-label="view pricing"
          >
            <FormattedMessage
              id="SUBSTANCE_VIEW_PRICING"
              defaultMessage="View Pricing"
            />
          </ButtonBase>
        </div>
      )}
      {loading ? (
        <CircleLoader />
      ) : (
        showModal && (
          <ResponsiveProductPAModal
            onClose={onToggleModal}
            substanceName={substanceName}
            productNumber={product.productNumber}
            productKey={product.productKey}
            brandKey={product.brand.key}
            description={product.description}
            materialIds={product.materialIds}
            isMarketplace={product.isMarketplace}
            productAttributes={product.attributes}
            marketplaceSellerId={product.marketplaceSellerId || ''}
            marketplaceOfferId={product.marketplaceOfferId || ''}
            erp_type={product.erp_type}
            productName={product.name ?? ''}
            displaySellerName={product.displaySellerName ?? ''}
            pricingData={data?.getPricingForProduct}
            paMessage={product.paMessage}
          />
        )
      )}
    </ListItem>
  )
}

export default ResponsiveSubstanceProduct
